/* eslint-disable no-throw-literal */
// AuthContext.js
import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { useDispatch } from "react-redux";
import { resolveApiUrl } from "../api/apiBase";
import { notificationsApi } from "../api/notificationsApi";
import { analyticsApi } from "../api/analyticsApi";
import { usersApi } from "../api/usersApi";
import { printingApi } from "../api/printingApi";
import { clearSelection } from "./printCenter/state/printSelectionSlice";
import {
  clearLocalFirebaseNotificationState,
  unregisterFirebaseNotificationToken,
} from "../layouts/Admin/Firebase";

const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const dispatch = useDispatch();
  const [token, setToken] = useState(localStorage.getItem("token") || null);
  const [loading, setLoading] = useState(true);

  // Identity, analytics and print data belong to the current sign-in.
  const resetSessionData = () => {
    dispatch(analyticsApi.util.resetApiState());
    dispatch(usersApi.util.resetApiState());
    dispatch(printingApi.util.resetApiState());
    dispatch(clearSelection());
  };

  const login = async (email, password) => {
    const previousToken =
      typeof localStorage !== "undefined"
        ? localStorage.getItem("token")
        : null;

    try {
      const { data: response } = await axios.post(
        resolveApiUrl("/authentication/login"),
        {
          email,
          password,
        }
      );
      const newToken = response.data;

      if (previousToken) {
        await unregisterFirebaseNotificationToken({
          authTokenOverride: previousToken,
        });
      }

      localStorage.setItem("token", newToken);
      setToken(newToken);
      clearLocalFirebaseNotificationState();
      dispatch(notificationsApi.util.resetApiState());
      resetSessionData();
      return true; // Indicate successful login
    } catch (error) {
      console.error("Login failed:", error);
      return false;
    }
  };

  const logout = async () => {
    const currentToken =
      typeof localStorage !== "undefined"
        ? localStorage.getItem("token")
        : token;

    try {
      if (currentToken) {
        await unregisterFirebaseNotificationToken({
          authTokenOverride: currentToken,
        });
      }
    } finally {
      setToken(null);
      localStorage.removeItem("token");
      clearLocalFirebaseNotificationState();
      dispatch(notificationsApi.util.resetApiState());
      resetSessionData();
    }
  };

  const checkToken = async () => {
    const userUrl = resolveApiUrl("/authentication/user");
    const storedToken =
      typeof localStorage !== "undefined"
        ? localStorage.getItem("token")
        : null;

    try {
      await axios.get(userUrl, {
        headers: {
          Authorization: `Bearer ${storedToken || token}`,
        },
      });
    } catch (error) {
      const status = error?.response?.status;
      const hasStoredToken = Boolean(storedToken);

      console.warn("[auth] checkToken failed", {
        status: status ?? null,
        message: error?.message ?? String(error),
        url: userUrl,
        hasStoredToken,
      });

      if (status === 401 || status === 403) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      setLoading(true);
      checkToken();
    } else {
      setLoading(false);
    }
  }, [token]);

  return (
    <AuthContext.Provider value={{ token, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => {
  return useContext(AuthContext);
};

export { AuthProvider, useAuth };
